/*
  Important: Only import pure types, only define pure data and pure types.

  This file is used in both in runtime code and Serverless definitions.
*/

/**
 * Valid values for the MIMIR_STAGE environment variable.
 */
export const stageIds = [
  'apac',
  'dev',
  'prod',
  'prod-vir',
  'prod-syd',
  'qa',
  'research',
  'research2',
  'research3',
  'research4',
  'research5',
  'research6',
  'research7',
  'research8',
  'research9',
  'stg2',
  'qa1',
  'qa2',
  'qa3',
  'qa4',
  'qa5',
  'nbcu-stg-east',
  'nbcu-prod-east',
  'prod-ohio',
] as const;

/**
 * Type for valid values for the MIMIR_STAGE environment variable.
 */
export type MimirStage = typeof stageIds[number];

export type ExternalIdConfig = {
  from: MimirStage,
  to: MimirStage
};


/**
 * Values that are not valid MIMIR_STAGE values, but are are frequently used
 * informal aliases.
 */
export const stageAliases: Partial<Record<MimirStage, string[]>> = {
  prod: ['prods', 'prod-multi-stack'],
  'prod-vir': ['us'],
  'prod-syd': ['oceania'],
  research: ['research1', 'r1'],
  research2: ['r2'],
  research3: ['r3'],
  research4: ['r4'],
  research5: ['r5'],
  research6: ['r6'],
  research7: ['r7'],
  research8: ['r8'],
  research9: ['r9'],
  stg2: ['stg', 'staging'],
};

/**
 * Values for the domain portion of environment variable MIMIR_HOME_URL that
 * cannot be directly derived from the MIMIR_STAGE value.
 */
export const customMimirHomeDomains: Partial<Record<MimirStage, string>> = {
  apac: 'apac.mjoll.no',
  prod: 'mimir.mjoll.no',
  'prod-vir': 'us.mjoll.no',
  'prod-syd': 'oceania.mjoll.no',
  qa: 'qa.mimir.tv',
  stg2: 'staging.mimir.tv',
  'nbcu-stg-east': 'mimir-east.up-stg.nbcuni.com',
  'nbcu-prod-east': 'mimir-east.up.nbcuni.com',
  'prod-ohio': 'us-east-2.mimir.media',
};


/**
 * Domains that we for legacy reasons need to support in addition to the main
 * domain of the stage.
 */
export const legacyMimirHomeDomains: Partial<Record<MimirStage, string[]>> = {
  prod: [
    'mimir.tv2fyn.dk',
    'www.mimirprod.com',
    'mimir.gbnews.uk',
  ],
  'prod-vir': [
    'foxnews.mjoll.no',
    'cnn.mjoll.no',
    'featurestory.mjoll.no'
  ],
  'prod-syd': [
    'skynews.mjoll.no',
    'nsw.mjoll.no'
  ],
  stg2: [
    'stg.mimir.tv'
  ]
};

export const delegatedAuthStages: Partial<Record<MimirStage, MimirStage>> = {
  'qa': 'dev',
  'research': 'dev',
  'research4': 'dev',
  'stg2': 'dev',
};

export const pipelineNameByStage: Record<MimirStage, string> = {
  'research': 'ResearchStack',
  'research2': 'Research2',
  'research3': 'Research3',
  'research4': 'Research4',
  'research5': 'Research5',
  'research6': 'Research6',
  'research7': 'research7',
  'research8': 'research8',
  'research9': 'research9',
  'apac': 'apac',
  'dev': 'Development',
  'qa': 'QA',
  'qa1': 'QA1',
  'qa2': 'QA2',
  'qa3': 'QA3',
  'qa4': 'QA4',
  'qa5': 'QA5',
  'nbcu-stg-east': 'NBCUStagingEast',
  'nbcu-prod-east': 'NBCUProdEast',
  'stg2': 'staging-sto',
  'prod': 'MimirMultiStackProd',
  'prod-vir': 'MimirVirginiaProd',
  'prod-syd': 'MimirSydneyProd',
  'prod-ohio': 'MimirOhioProd',
};

export const awsAccounts = <const>{
  dev: '768998751122',
  production: '265767647558',
  staging: '960306730984',
  qa: '845997328869',
  nbcuStaging: '686410906178',
  nbcuProd: '655593807570',
  mimirOhioProd: '476189860076',
};

export type MimirAwsAccountId = typeof awsAccounts[keyof typeof awsAccounts];


export interface StageLocation {
  /**
   * Value corresponding to the AWS_ACCOUNT_ID environment variable.
   */
  awsAccountId: string;

  /**
   * Value corresponding to the MIMIR_REGION environment variable.
   */
  mimirRegion: string;
}

export const stageLocations: Record<MimirStage, StageLocation> = {
  'apac': {
    awsAccountId: awsAccounts.production,
    mimirRegion: 'ap-southeast-1',
  },
  'dev': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'eu-west-1',
  },
  'prod': {
    awsAccountId: awsAccounts.production,
    mimirRegion: 'eu-west-1',
  },
  'prod-vir': {
    awsAccountId: awsAccounts.production,
    mimirRegion: 'us-east-1',
  },
  'prod-syd': {
    awsAccountId: awsAccounts.production,
    mimirRegion: 'ap-southeast-2',
  },
  'qa': {
    awsAccountId: awsAccounts.staging,
    mimirRegion: 'eu-west-1',
  },
  'research': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'eu-west-1',
  },
  'research2': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'eu-west-1',
  },
  'research3': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'eu-west-1',
  },
  'research4': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'eu-west-1',
  },
  'research5': {
    awsAccountId: awsAccounts.staging,
    mimirRegion: 'eu-west-1',
  },
  'research6': {
    awsAccountId: awsAccounts.staging,
    mimirRegion: 'eu-west-1',
  },
  'research7': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'us-east-1',
  },
  'research8': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'ap-southeast-2',
  },
  'research9': {
    awsAccountId: awsAccounts.dev,
    mimirRegion: 'ap-southeast-1',
  },
  'stg2': {
    awsAccountId: awsAccounts.staging,
    mimirRegion: 'eu-west-1',
  },
  qa1: {
    awsAccountId: awsAccounts.qa,
    mimirRegion: 'eu-west-1',
  },
  qa2: {
    awsAccountId: awsAccounts.qa,
    mimirRegion: 'ap-south-1',
  },
  qa3: {
    awsAccountId: awsAccounts.qa,
    mimirRegion: 'ap-southeast-1',
  },
  qa4: {
    awsAccountId: awsAccounts.qa,
    mimirRegion: 'ap-southeast-1',
  },
  qa5: {
    awsAccountId: awsAccounts.qa,
    mimirRegion: 'ap-south-1',
  },
  'nbcu-stg-east': {
    awsAccountId: awsAccounts.nbcuStaging,
    mimirRegion: 'us-east-1',
  },
  'nbcu-prod-east': {
    awsAccountId: awsAccounts.nbcuProd,
    mimirRegion: 'us-east-1',
  },
  'prod-ohio': {
    awsAccountId: awsAccounts.mimirOhioProd,
    mimirRegion: 'us-east-2',
  },
};

export interface UserPoolInfo {
  id: string;
  name: string;
}

export const userPools: Record<MimirStage, UserPoolInfo | null> = {
  'apac': { id: 'ap-southeast-1_ZN0Y2kAkT', name: 'user-pool-mimir-apac' },
  'dev': { id: 'eu-west-1_zCllLNlVD', name: 'MediaCoreUserPool' },
  'prod': { id: 'eu-west-1_4dvu4whks', name: 'ProdUsers' },
  'prod-syd': { id: 'ap-southeast-2_OjKcmY6oi', name: 'MimirUserPool' },
  'prod-vir': { id: 'us-east-1_aWAhnbLZD', name: 'MimirUserPool' },
  'qa': null,
  'research': null,
  'research2': { id: 'eu-west-1_pI2y1cBMx', name: 'MimirDevFederated' },
  'research3': { id: 'eu-west-1_nKMI9X57H', name: 'MimirResearch3' },
  'research4': null,
  'research5': { id: 'eu-west-1_jXI1lpZVJ', name: 'MimirUserPool' },
  'research6': { id: 'eu-west-1_0ICbUnhF6', name: 'r6UserPool' },
  'research7': { id: 'us-east-1_ZkPbB7SuW', name: 'user-pool-mimir-research7' },
  'research8': { id: 'ap-southeast-2_Ali8roL4h', name: 'user-pool-mimir-research8' },
  'research9': { id: 'ap-southeast-1_7YUg5mHYZ', name: 'user-pool-mimir-research9' },
  'stg2': null,
  'qa1': { id: 'eu-west-1_K1CDRorbl', name: 'user-pool-mimir-qa1'},
  'qa2': { id: 'ap-south-1_5mYiHRGN8', name: 'user-pool-mimir-qa2' },
  'qa3': { id: 'ap-southeast-1_gU0OZN5Gi', name: 'user-pool-mimir-qa3' },
  'qa4': { id: 'ap-southeast-1_XHKuUlAuB', name: 'user-pool-mimir-qa4' },
  'qa5': { id: 'ap-south-1_Q1HJWXw4w', name: 'user-pool-mimir-qa5' },
  'nbcu-stg-east': { id: 'us-east-1_OHwz46qhR', name: 'user-pool-mimir-nbcu-stg-east' },
  'nbcu-prod-east': { id: 'us-east-1_hMXSahfd9', name: 'user-pool-mimir-nbcu-prod-east' },
  'prod-ohio': { id: 'us-east-2_5krju1GU3', name: 'user-pool-mimir-nbcu-prod-east' },
};

export const productionStages: MimirStage[] = ['prod', 'prod-vir', 'prod-syd', 'apac', 'prod-ohio'];

/**
 * The domain used for the certificate in ACM.
 * This is used to determine which certificate to look in ACM.
 * The default value is `*.mimir.tv`, but for production stages, we use `mimir.media`.
 */
const DEFAULT_CERTIFICATE_DOMAIN = '*.mimir.tv';
const PROD_CERTIFICATE_DOMAIN = 'mimir.media';

export const certificatePerStage: Record<MimirStage, string> = {
  apac: PROD_CERTIFICATE_DOMAIN,
  prod: PROD_CERTIFICATE_DOMAIN,
  'prod-vir': PROD_CERTIFICATE_DOMAIN,
  'prod-syd': PROD_CERTIFICATE_DOMAIN,
  qa: DEFAULT_CERTIFICATE_DOMAIN,
  dev: DEFAULT_CERTIFICATE_DOMAIN,
  research: DEFAULT_CERTIFICATE_DOMAIN,
  research2: DEFAULT_CERTIFICATE_DOMAIN,
  research3: DEFAULT_CERTIFICATE_DOMAIN,
  research4: DEFAULT_CERTIFICATE_DOMAIN,
  research5: DEFAULT_CERTIFICATE_DOMAIN,
  research6: DEFAULT_CERTIFICATE_DOMAIN,
  research7: DEFAULT_CERTIFICATE_DOMAIN,
  research8: DEFAULT_CERTIFICATE_DOMAIN,
  research9: DEFAULT_CERTIFICATE_DOMAIN,
  stg2: DEFAULT_CERTIFICATE_DOMAIN,
  qa1: DEFAULT_CERTIFICATE_DOMAIN,
  qa2: DEFAULT_CERTIFICATE_DOMAIN,
  qa3: DEFAULT_CERTIFICATE_DOMAIN,
  'nbcu-stg-east': DEFAULT_CERTIFICATE_DOMAIN,
  'nbcu-prod-east': PROD_CERTIFICATE_DOMAIN,
  qa4: DEFAULT_CERTIFICATE_DOMAIN,
  qa5: DEFAULT_CERTIFICATE_DOMAIN,
  'prod-ohio': PROD_CERTIFICATE_DOMAIN,
};
